// StatusButton — dropdown in the top bar combining the three primary
// authoring actions:
//   • Save Draft        → save as 'draft'
//   • Publish Now       → save as 'published' (gated by validation)
//   • Schedule for…     → opens datetime picker, saves as 'scheduled'
//
// Disabled state of "Publish" is driven by `canPublish` — the parent
// computes this from validateBlock + title/summary checks.

import { useEffect, useRef, useState } from "react";
import { t } from "./theme";

export interface StatusButtonProps {
  canPublish: boolean;
  canPublishReason: string | null;
  onSaveDraft: () => void;
  onPublishNow: () => void;
  onSchedule: (datetimeLocal: string) => void;
}

export function StatusButton({
  canPublish,
  canPublishReason,
  onSaveDraft,
  onPublishNow,
  onSchedule,
}: StatusButtonProps) {
  const [open, setOpen] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [scheduleVal, setScheduleVal] = useState("");
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setScheduling(false);
      }
    }
    if (open) {
      const tm = setTimeout(() => document.addEventListener("mousedown", onDown), 0);
      return () => {
        clearTimeout(tm);
        document.removeEventListener("mousedown", onDown);
      };
    }
  }, [open]);

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "7px 12px",
          background: canPublish ? t.accent : t.cardRaised,
          color: canPublish ? "#1c1c1e" : t.text3,
          border: "none",
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 600,
          fontFamily: t.sans,
          cursor: "pointer",
        }}
      >
        Publish ▾
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            minWidth: 260,
            background: t.card,
            color: t.text,
            border: `1px solid ${t.border}`,
            borderRadius: 8,
            boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
            padding: 6,
            zIndex: 50,
            fontFamily: t.sans,
          }}
        >
          {scheduling ? (
            <div style={{ padding: 8 }}>
              <div style={{ fontSize: 11, color: t.text3, marginBottom: 6 }}>
                Pick a publish date:
              </div>
              <input
                type="datetime-local"
                value={scheduleVal}
                onChange={(e) => setScheduleVal(e.target.value)}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: "6px 8px",
                  background: t.bg,
                  border: `1px solid ${t.border}`,
                  borderRadius: 4,
                  fontSize: 12,
                  color: t.text,
                  outline: "none",
                  marginBottom: 8,
                }}
              />
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  type="button"
                  onClick={() => setScheduling(false)}
                  style={ghost}
                >
                  Back
                </button>
                <button
                  type="button"
                  disabled={!scheduleVal}
                  onClick={() => {
                    onSchedule(scheduleVal);
                    setOpen(false);
                    setScheduling(false);
                  }}
                  style={{ ...primary, opacity: scheduleVal ? 1 : 0.5 }}
                >
                  Schedule
                </button>
              </div>
            </div>
          ) : (
            <>
              <MenuItem
                label="Save Draft"
                desc="Keep working — visible only to editors."
                onClick={() => {
                  onSaveDraft();
                  setOpen(false);
                }}
              />
              <MenuItem
                label="Publish Now"
                desc={canPublishReason || "Make this article live."}
                disabled={!canPublish}
                onClick={() => {
                  onPublishNow();
                  setOpen(false);
                }}
                emphasis
              />
              <MenuItem
                label="Schedule for…"
                desc="Auto-publish at a chosen time."
                disabled={!canPublish}
                onClick={() => setScheduling(true)}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function MenuItem({
  label,
  desc,
  onClick,
  disabled,
  emphasis,
}: {
  label: string;
  desc: string;
  onClick: () => void;
  disabled?: boolean;
  emphasis?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      style={{
        display: "block",
        width: "100%",
        textAlign: "left",
        padding: "8px 10px",
        background: "transparent",
        border: "none",
        borderRadius: 5,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.5 : 1,
        transition: t.transition,
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        (e.currentTarget as HTMLButtonElement).style.background = t.hover;
      }}
      onMouseLeave={(e) =>
        ((e.currentTarget as HTMLButtonElement).style.background = "transparent")
      }
    >
      <div
        style={{
          fontSize: 13,
          fontWeight: emphasis ? 600 : 500,
          color: emphasis ? t.accent : t.text,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 11, color: t.text3, marginTop: 2 }}>{desc}</div>
    </button>
  );
}

const ghost: React.CSSProperties = {
  flex: 1,
  padding: "6px 10px",
  background: "transparent",
  border: `1px solid ${t.border}`,
  borderRadius: 4,
  fontSize: 12,
  color: t.text2,
  cursor: "pointer",
};

const primary: React.CSSProperties = {
  flex: 1,
  padding: "6px 10px",
  background: t.accent,
  color: "#1c1c1e",
  border: "none",
  borderRadius: 4,
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
};
